import { useEffect } from 'react'
import MessageInformation from '../MessageDetail/Accordion/MessageInformation'
import MessageDetailHeader from '../MessageDetail/MessageDetailHeader'
import MessageSummary from '../MessageDetail/MessageSummary'
import { useMessagesDashboardViewContext } from '../MessagesDashboardViewContext'
import { Drawer, LinearProgress, Paper, Stack } from '@mui/material'
import isEmpty from 'lodash.isempty'
import SingleMessageActionSelector from './SingleMessageActionSelector'
import { useSearchActions } from '../api'

export interface MessageQuickViewProps {
  open: boolean
  onClose?: () => void
  messageId?: string
}

const MessageQuickView = ({ open, messageId, onClose }: MessageQuickViewProps) => {
  const context = useMessagesDashboardViewContext()
  const message = context.messageDetails.message
  const isLoading = context.messageDetails.isLoading
  const { data: actions } = useSearchActions()

  useEffect(() => {
    if (messageId && !isEmpty(messageId)) {
      context.readMessageDetails(messageId)
    }
  }, [messageId])

  // Clear detail message when drawer closes
  useEffect(() => {
    if (!open) {
      context.setDetailMessage(undefined)
    }
  }, [open]) // Removed context dependency to prevent infinite loop

  const handleClose = () => {
    // Clear detail message when closing
    context.setDetailMessage(undefined)
    onClose?.()
  }

  return (
    <Drawer
      data-testid="message-quick-view"
      open={open}
      onClose={handleClose}
      anchor="right"
      variant="persistent"
      PaperProps={{
        style: {
          width: '60%',
          overflowY: 'auto',
          boxShadow: '0px 0px 24px 8px rgba(0,0,0,0.25)', // Strong shadow
        },
      }}
    >
      <Stack>
        <div
          style={{
            height: '0px',
          }}
        >
          <LinearProgress
            data-testid="loading"
            style={{
              display: !isLoading ? 'none' : '',
            }}
          />
        </div>

        {message ? (
          <Stack
            style={{
              overflow: 'clip',
              height: '100%',
              paddingBottom: '80px',
            }}
            p={1}
            spacing={1}
          >
            <MessageDetailHeader
              message={message}
              showActions={true}
              onClose={handleClose}
              onReload={() => context.readMessageDetails(message.id, true)}
            />

            <Paper
              sx={{
                flexDirection: 'column',
                padding: 2,
              }}
            >
              <MessageSummary message={message} />
            </Paper>

            <Paper sx={{ padding: 2 }}>
              <SingleMessageActionSelector actions={actions} />
            </Paper>

            <Paper
              sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: '100%',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <MessageInformation message={message} />
            </Paper>
          </Stack>
        ) : null}
      </Stack>
    </Drawer>
  )
}

export default MessageQuickView
